// Contrato de datos ERP → Informe Tributario Integral (v1.0): construcción del objeto E y validaciones V00–V12.
// La carpeta tributaria (C) y el certificado TGR (T) son la fuente oficial; E solo complementa.

export const VERSION_CONTRATO = "1.0";
const IVA = 0.19;
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const n = (v) => (v === "" || v === null || v === undefined || isNaN(+v) ? 0 : +v);
const has = (v) => !(v === "" || v === null || v === undefined || isNaN(+v));
const r0 = (v) => Math.round(n(v));
const clp = (v) => (v < 0 ? "-" : "") + "$" + Math.round(Math.abs(v || 0)).toLocaleString("es-CL");
const pct = (v, d = 1) => `${(v * 100).toLocaleString("es-CL", { maximumFractionDigits: d })}%`;
const tol = (v) => Math.max(1000, Math.abs(v) * 0.01);
const mesNum = (p) => +String(p || "").slice(5, 7);
const nombreMes = (m) => MESES[m - 1] || "";
const listaMeses = (ms) => {
  const nombres = ms.map(nombreMes);
  return nombres.length > 1 ? `${nombres.slice(0, -1).join(", ")} y ${nombres[nombres.length - 1]}` : nombres[0] || "";
};
const rango = (m) => (m <= 0 ? "—" : m === 1 ? "enero" : `enero a ${nombreMes(m)}`);
const sinUndef = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));

export function rutContrato(r) {
  const s = String(r || "").replace(/[^0-9kK]/g, "").toUpperCase();
  return s.length > 1 ? `${s.slice(0, -1)}-${s.slice(-1)}` : null;
}

export const asesorGenerico = (nombre) => {
  const s = String(nombre || "").trim();
  return !s || /admin|^(usuario|user|demo|test|prueba|soporte|contador|owner|superadmin)$/i.test(s);
};

// ---------- Objeto E a partir de los datos que entrega el backend (/informes-tributarios/datos-erp) ----------
export function construirE(X, P, factores) {
  if (!X) return null;
  const anio = +X.anio;
  const delAnio = (obj) => Object.keys(obj || {}).filter((p) => p.startsWith(`${anio}-`)).sort();
  const v2 = (X.version_datos || 0) >= 2;
  const cont = X.contabilidad;
  const hayConta = (cont?.asientos || 0) > 0;

  const E = {
    version: VERSION_CONTRATO,
    rut: rutContrato(X.empresa?.rut),
    anioComercial: anio,
    fechaCorte: X.hasta,
    ultimoPeriodoCentralizado: P?.ultimoPeriodoCentralizado || cont?.centralizacion?.ultimo || null,
    generadoEn: X.generado,
    asesor: { nombre: String(P?.asesor || "").trim() || null, estudio: P?.estudio || null, web: "www.aequor.cl" },
  };

  if (v2) {
    E.libroHonorarios = (X.libro_honorarios || []).map((d) =>
      sinUndef({
        periodo: d.periodo,
        tipo: d.tipo === "BTE" ? "BTE" : "BHE",
        folio: d.folio,
        rutPrestador: rutContrato(d.rut) || d.rut,
        nombrePrestador: d.nombre || undefined,
        bruto: r0(d.bruto),
        retencion: r0(d.retencion),
        fechaPago: d.fecha_pago || undefined,
      })
    );
    E.rcv = {
      ventas: delAnio(X.ventas).map((p) => {
        const m = X.ventas[p];
        return {
          periodo: p, netoAfecto: r0(m.doc_neto), exento: r0(m.exento), iva: r0(m.doc_iva),
          ncNeto: r0(m.nc_neto), ncIva: r0(m.nc_iva), ndNeto: r0(m.nd_neto), ndIva: r0(m.nd_iva), cantidadDocumentos: r0(m.docs),
        };
      }),
      compras: delAnio(X.compras).map((p) => {
        const m = X.compras[p];
        return {
          periodo: p, netoGiro: r0(m.doc_neto), ivaRecuperable: r0(m.doc_iva), ivaNoRecuperable: r0(m.iva_no_recuperable),
          ivaUsoComun: r0(m.iva_uso_comun), activoFijoNeto: r0(m.af_neto), activoFijoIva: r0(m.af_iva),
          ncNeto: r0(m.nc_neto), ncIva: r0(m.nc_iva), ndNeto: r0(m.nd_neto), ndIva: r0(m.nd_iva), exento: r0(m.exento),
          cantidadDocumentos: r0(m.docs),
        };
      }),
    };
    E.activoFijo = (X.activo_fijo || []).map((d) =>
      sinUndef({
        id: d.id,
        descripcion: d.descripcion,
        folioFactura: d.folio,
        rutProveedor: rutContrato(d.rut) || d.rut,
        fechaFactura: d.fecha,
        neto: r0(d.neto),
        iva: r0(d.iva),
        fechaPago: d.fecha_pago || undefined,
        montoPagado: has(d.monto_pagado) ? r0(d.monto_pagado) : undefined,
      })
    );
  }

  if (hayConta) {
    const bal = cont.balance || {};
    const s = cont.saldos;
    const saldos = s
      ? {
          cajaBancos: r0(bal.caja?.total), clientes: r0(bal.clientes?.total),
          activoFijoBruto: r0(s.activo_fijo_bruto), depreciacionAcumulada: r0(s.depreciacion_acumulada),
          activoFijoNeto: r0(s.activo_fijo_bruto) - r0(s.depreciacion_acumulada),
          proveedores: r0(bal.proveedores?.total), impuestosPorPagar: r0(s.impuestos_por_pagar), prestamos: r0(bal.prestamos?.total),
          capitalPagado: r0(bal.capital?.total), retirosSocios: r0(cont.flujos?.retiros_socios), patrimonio: r0(s.patrimonio),
          // Extensiones para los controles V02 y V08
          activoFijoBrutoInicio: r0(s.activo_fijo_bruto_inicio), totalActivos: r0(s.total_activos), totalPasivos: r0(s.total_pasivos),
        }
      : {
          cajaBancos: r0(bal.caja?.total), clientes: r0(bal.clientes?.total), activoFijoNeto: r0(bal.activo_fijo?.total),
          proveedores: r0(bal.proveedores?.total), prestamos: r0(bal.prestamos?.total), capitalPagado: r0(bal.capital?.total),
        };
    const hasta = mesNum(X.hasta) || 12;
    const resultadosPorMes = [];
    for (let m = 1; m <= hasta; m++) {
      const p = `${anio}-${String(m).padStart(2, "0")}`;
      const fila = { periodo: p, ventas: 0, costoVentas: 0, gastosAdministracion: 0, remuneraciones: 0, depreciacion: 0, otrosIngresos: 0, otrosGastos: 0 };
      const filas = cont.resultado || [];
      const hayOperacional = filas.some((f) => f.tipo === "ingreso" && (f.codigo || "").startsWith("4.1"));
      let mov = false;
      for (const f of filas) {
        const v = n(f.meses?.[p]);
        if (!v) continue;
        mov = true;
        if (f.tipo === "ingreso") {
          if (!hayOperacional || (f.codigo || "").startsWith("4.1")) fila.ventas += v;
          else fila.otrosIngresos += v;
        } else if (f.categoria === "costo") fila.costoVentas += v;
        else if (f.categoria === "remuneraciones") fila.remuneraciones += v;
        else if (f.categoria === "depreciacion") fila.depreciacion += v;
        else if (f.categoria === "impuesto_renta") fila.otrosGastos += v;
        else fila.gastosAdministracion += v;
      }
      if (mov) resultadosPorMes.push(Object.fromEntries(Object.entries(fila).map(([k, v]) => [k, k === "periodo" ? v : Math.round(v)])));
    }
    E.contabilidad = { saldos, resultadosPorMes };
    if (cont.flujos) {
      E.contabilidad.flujosAcumulados = { cobrosClientes: r0(cont.flujos.cobros_clientes), pagosProveedores: r0(cont.flujos.pagos_proveedores) };
    }
  }

  E.remuneraciones = delAnio(X.remuneraciones).map((p) => {
    const q = X.remuneraciones[p];
    return { periodo: p, trabajadores: r0(q.trabajadores), totalHaberes: r0(q.haberes), leyesSocialesEmpleador: r0(q.costo_empleador), impuestoUnico: r0(q.impuesto_unico) };
  });

  const ajuste = (concepto, monto) => (n(monto) > 0 ? [{ concepto, monto: r0(monto) }] : []);
  E.ajustes = {
    gastosSinIVA: ajuste("Gastos sin IVA del año (panel)", P?.gastosSinIVA),
    agregados: ajuste("Agregados a la base (panel)", P?.agregados),
    deducciones: ajuste("Deducciones a la base (panel)", P?.deducciones),
    retirosSocios: ajuste("Retiros estimados de los socios (panel)", P?.retirosEstimados),
  };
  if (factores && Object.keys(factores).length) E.factoresActualizacion = factores;
  return E;
}

// ---------- Validaciones previas a la emisión ----------
const V = (id, control, estado, o = {}) => ({
  id,
  control,
  estado,
  valorERP: o.valorERP ?? null,
  valorOficial: o.valorOficial ?? null,
  diferencia: o.diferencia !== undefined ? o.diferencia : has(o.valorERP) && has(o.valorOficial) ? Math.round(o.valorERP - o.valorOficial) : null,
  mensajeInterno: o.mensajeInterno || "",
  mensajeCliente: o.mensajeCliente || null,
  accion: o.accion || null,
  seccion: o.seccion || null,
});

export function validaciones({ C, T, P, E, X, decl, ultimoMes, rk }) {
  const out = [];
  const f29 = (r, c) => n(r.f?.codes?.[c]);
  const sumF29 = (c) => decl.reduce((s, r) => s + f29(r, c), 0);
  const enCorte = (p) => p && +String(p).slice(0, 4) === +E?.anioComercial && mesNum(p) <= ultimoMes;
  const s = E?.contabilidad?.saldos;

  // V00 Identidad
  {
    const rc = rutContrato(C?.rut);
    const rt = T ? rutContrato(T.rut) : null;
    const re = E?.rut || null;
    const distintos = [re && re !== rc ? `Aequor ${re}` : null, rt && rt !== rc ? `certificado TGR ${rt}` : null].filter(Boolean);
    out.push(
      V("V00", "Identidad de la empresa", distintos.length ? "BLOQUEO" : "OK", {
        mensajeInterno: distintos.length
          ? `El RUT de la carpeta tributaria (${rc}) no coincide con: ${distintos.join(" y ")}.`
          : `RUT ${rc} igual en la carpeta tributaria${rt ? ", el certificado TGR" : ""}${re ? " y Aequor" : ""}.`,
        accion: distintos.length ? "Cargar la carpeta y el certificado de la empresa correcta, o cambiar la empresa activa." : null,
      })
    );
  }

  // V01 Honorarios: libro vs código 151
  {
    const control = "Libro de honorarios vs retenciones F29 (cód. 151)";
    const total151 = sumF29("151");
    // Informes guardados antes del contrato: solo hay totales mensuales del libro.
    const libroBase =
      E?.libroHonorarios ||
      (X?.honorarios
        ? Object.entries(X.honorarios).map(([periodo, h]) => ({ periodo, tipo: "BHE", bruto: n(h.bruto), retencion: n(h.retencion), docs: n(h.docs) || 1 }))
        : null);
    if (!libroBase) {
      out.push(V("V01", control, "OMITIDA", { mensajeInterno: "Sin libro de honorarios de Aequor: la proyección estima el bruto desde el código 151 (retención ÷ tasa)." }));
    } else {
      const libro = libroBase.filter((d) => enCorte(d.periodo));
      const nDocs = libro.reduce((a, d) => a + (d.docs || 1), 0);
      const porMes = {};
      libro.forEach((d) => {
        const m = mesNum(d.periodo);
        porMes[m] = porMes[m] || { ret: 0, bruto: 0 };
        porMes[m].ret += n(d.retencion);
        porMes[m].bruto += n(d.bruto);
      });
      const ret = libro.reduce((a, d) => a + n(d.retencion), 0);
      const bruto = libro.reduce((a, d) => a + n(d.bruto), 0);
      const bte = libro.filter((d) => d.tipo === "BTE").length;
      if (!libro.length && !total151) {
        out.push(V("V01", control, "OK", { valorERP: 0, valorOficial: 0, mensajeInterno: "Sin boletas en el libro ni retenciones en el F29." }));
      } else if (!libro.length) {
        out.push(
          V("V01", control, "ADVERTENCIA", {
            valorERP: 0, valorOficial: total151,
            mensajeInterno: `El F29 informa ${clp(total151)} de retenciones (código 151), pero el libro de honorarios de Aequor no tiene boletas del año. La proyección usa la estimación desde el F29.`,
            accion: "Importar las boletas de honorarios recibidas (BHE) y las de terceros emitidas (BTE).",
          })
        );
      } else {
        const difMeses = decl.filter((r) => Math.abs(n(porMes[r.mes]?.ret) - f29(r, "151")) > tol(f29(r, "151"))).map((r) => r.mes);
        const dif = ret - total151;
        const rel = total151 ? Math.abs(dif) / total151 : 1;
        const estado = rel > 0.2 && Math.abs(dif) > tol(total151) ? "BLOQUEO" : difMeses.length ? "ADVERTENCIA" : "OK";
        const faltaBTE = dif < 0 && !bte;
        out.push(
          V("V01", control, estado, {
            valorERP: ret, valorOficial: total151, diferencia: Math.round(dif),
            mensajeInterno:
              `Libro: ${nDocs} boletas${bte ? ` (${bte} BTE)` : ""}, bruto ${clp(bruto)} → retención ${clp(ret)}. Código 151 acumulado: ${clp(total151)}.` +
              (estado === "OK" ? " Cuadra." : ` Diferencia de ${clp(Math.abs(dif))} (${pct(rel, 0)}).${difMeses.length ? ` No cuadra en ${listaMeses(difMeses)}.` : ""}`) +
              (estado === "OK" ? "" : " La proyección usa la estimación desde el F29."),
            accion:
              estado === "OK"
                ? null
                : `Revisar si faltan boletas en el libro${faltaBTE ? " o si hay BTE retenidas por la empresa" : ""}${difMeses.length ? ` (${listaMeses(difMeses)})` : ""}.`,
          })
        );
      }
    }
  }

  // V02 Activo fijo contable vs F29 (código 525)
  {
    const control = "Activo fijo contable vs F29 (cód. 525)";
    const oficial = Math.round(decl.reduce((a, r) => a + f29(r, "525") / IVA, 0));
    if (!s || (!has(s.activoFijoNeto) && !has(s.activoFijoBruto))) {
      out.push(V("V02", control, "OMITIDA", { valorOficial: oficial, mensajeInterno: "Sin saldos contables en Aequor: la depreciación instantánea usa el código 525 del F29." }));
    } else {
      const bruto = has(s.activoFijoBruto) ? n(s.activoFijoBruto) : n(s.activoFijoNeto) + n(s.depreciacionAcumulada);
      const inicio = n(s.activoFijoBrutoInicio);
      const erp = bruto - inicio;
      const claves = {};
      (E.activoFijo || []).forEach((a) => {
        const k = `${a.folioFactura}|${a.rutProveedor}`;
        claves[k] = (claves[k] || 0) + 1;
      });
      const repetidos = Object.entries(claves).filter(([, c]) => c > 1).map(([k]) => k.split("|")[0]);
      // Asientos de activo fijo con el mismo monto: el duplicado típico (factura + asiento manual).
      const movs = X?.contabilidad?.movimientos_af || [];
      const porMonto = {};
      movs.forEach((m) => (porMonto[m.monto] = [...(porMonto[m.monto] || []), m]));
      const dupMov = Object.values(porMonto).find((g) => g.length > 1 && g[0].monto > 0);
      const ratio = oficial > 0 ? erp / oficial : erp > tol(0) ? Infinity : 1;
      const cuadra = Math.abs(erp - oficial) <= tol(oficial);
      const estado = cuadra && !repetidos.length ? "OK" : ratio >= 1.8 || repetidos.length ? "BLOQUEO" : "ADVERTENCIA";
      const mesDup = dupMov ? nombreMes(mesNum(dupMov[0].fecha)) : null;
      out.push(
        V("V02", control, estado, {
          valorERP: erp, valorOficial: oficial,
          mensajeInterno:
            `Activo fijo contable${inicio ? ` comprado en el año (bruto ${clp(bruto)} − saldo inicial ${clp(inicio)})` : ` (neto ${clp(n(s.activoFijoNeto))} + depreciación ${clp(n(s.depreciacionAcumulada))})`}: ${clp(erp)}. ` +
            `Compras de activo fijo según F29 (código 525 ÷ 19%): ${clp(oficial)}.` +
            (oficial > 0 && !cuadra ? ` El contable es ${ratio.toLocaleString("es-CL", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} veces lo declarado.` : "") +
            (repetidos.length ? ` Facturas repetidas: folio ${repetidos.join(", ")}.` : "") +
            (dupMov ? ` Asientos con el mismo monto: ${dupMov.map((m) => `N°${m.numero} del ${m.fecha}`).join(" y ")} por ${clp(dupMov[0].monto)}.` : "") +
            (estado === "OK" ? "" : " La proyección usa el código 525 del F29."),
          accion:
            estado === "OK"
              ? null
              : repetidos.length
                ? `Eliminar las facturas de activo fijo repetidas (folio ${repetidos.join(", ")}).`
                : ratio >= 1.8
                  ? `Revisar el registro de activo fijo${mesDup ? ` de ${mesDup}` : ""} y eliminar el duplicado.`
                  : ratio < 1
                    ? "Activar en la contabilidad las compras de activo fijo declaradas en el F29."
                    : "Conciliar el activo fijo contable con las facturas del registro de compras.",
        })
      );
    }
  }

  // V03 Pagos a proveedores
  {
    const control = "Proveedores impagos vs compras del año";
    if (!s || !has(s.proveedores)) {
      out.push(V("V03", control, "OMITIDA", { mensajeInterno: "Sin saldos contables: se usa la base devengada del F29." }));
    } else {
      const comprasRCV = E.rcv?.compras?.length
        ? E.rcv.compras.reduce(
            (a, k) => a + n(k.netoGiro) + n(k.ivaRecuperable) + n(k.ivaNoRecuperable) + n(k.ivaUsoComun) + n(k.activoFijoNeto) + n(k.activoFijoIva) + n(k.exento) + n(k.ndNeto) + n(k.ndIva) - n(k.ncNeto) - n(k.ncIva),
            0
          )
        : null;
      const comprasF29 = Math.round(((sumF29("520") - sumF29("528") + sumF29("525")) / IVA) * (1 + IVA));
      const compras = comprasRCV !== null ? Math.round(comprasRCV) : comprasF29;
      const prov = n(s.proveedores);
      const caja = n(s.cajaBancos);
      const pagos = E.contabilidad?.flujosAcumulados?.pagosProveedores;
      if (compras <= 0) {
        out.push(V("V03", control, "OK", { valorERP: prov, valorOficial: 0, mensajeInterno: "Sin compras en el año." }));
      } else {
        const ratio = prov / compras;
        const estado = ratio > 0.8 ? "ADVERTENCIA" : "OK";
        const proPyme = rk === "14D3";
        out.push(
          V("V03", control, estado, {
            valorERP: prov, valorOficial: compras, diferencia: null,
            mensajeInterno:
              `Proveedores por pagar ${clp(prov)} = ${pct(ratio)} de las compras del año con IVA (${clp(compras)}${comprasRCV === null ? ", estimadas desde el F29" : ""}).` +
              (has(pagos) ? ` Pagos a proveedores registrados en el año: ${clp(pagos)}.` : " Sin datos de pagos del año.") +
              (estado !== "OK" && caja >= prov * 0.5 ? ` Caja alta (${clp(caja)}): probablemente faltan registrar pagos.` : "") +
              (estado !== "OK" && P?.excluirImpagas && proPyme ? " La proyección excluye las compras impagas (confirmado en el panel)." : ""),
            accion: estado === "OK" ? null : `Registrar los pagos a proveedores del año.${proPyme ? " Si de verdad están impagos, en Pro Pyme esos gastos no se rebajan hasta pagarse." : ""}`,
            mensajeCliente:
              estado !== "OK" && proPyme
                ? "En el régimen Pro Pyme, las compras y los gastos se rebajan del impuesto cuando están pagados. Según la contabilidad, buena parte de las compras del año figura impaga: las facturas que sigan sin pagar al 31 de diciembre no reducirán el impuesto."
                : null,
            seccion: "resultado",
          })
        );
      }
    }
  }

  // V04 Contabilidad al día
  {
    const control = "Contabilidad al día (ventas contables vs F29)";
    const res = E?.contabilidad?.resultadosPorMes;
    if (!res?.length || !decl.length) {
      out.push(V("V04", control, "OMITIDA", { mensajeInterno: "Sin resultados contables del año: se omite la conciliación de ventas." }));
    } else {
      const contable = res.reduce((a, r) => a + n(r.ventas), 0);
      const acum = { 0: 0 };
      let run = 0;
      decl.forEach((r) => {
        run += n(r.ventas);
        acum[r.mes] = run;
      });
      const candidatos = [0, ...decl.map((r) => r.mes)];
      const mStar = candidatos.reduce((best, m) => (Math.abs(contable - acum[m]) < Math.abs(contable - acum[best]) ? m : best), 0);
      const totalF29 = acum[ultimoMes] ?? run;
      const cuadraTotal = Math.abs(contable - totalF29) <= tol(totalF29);
      const upcTxt = E.ultimoPeriodoCentralizado;
      const upc = upcTxt ? (+upcTxt.slice(0, 4) < E.anioComercial ? 0 : +upcTxt.slice(0, 4) > E.anioComercial ? 12 : mesNum(upcTxt)) : null;
      const base = cuadraTotal ? ultimoMes : upc !== null ? Math.min(mStar, upc) : mStar;
      const faltan = [];
      for (let m = base + 1; m <= ultimoMes; m++) if (decl.some((r) => r.mes === m)) faltan.push(m);
      let estado = "OK";
      if (!cuadraTotal) {
        if (ultimoMes - base > 2) estado = "BLOQUEO";
        else if (upc === null || mStar < upc) estado = "ADVERTENCIA";
        else estado = "INFO";
      }
      out.push(
        V("V04", control, estado, {
          valorERP: contable, valorOficial: totalF29,
          mensajeInterno: cuadraTotal
            ? `Ventas contables ${clp(contable)} cuadran con el F29 de ${rango(ultimoMes)} (${clp(totalF29)}).`
            : `Ventas contables ${clp(contable)} ≈ F29 ${rango(mStar)} ${clp(acum[mStar])} · F29 ${rango(ultimoMes)}: ${clp(totalF29)}. ` +
              `La contabilidad cuadra aproximadamente hasta ${mStar ? nombreMes(mStar) : "antes de enero"}${faltan.length ? `; faltan ${listaMeses(faltan)}` : ""}. ` +
              (upcTxt ? `Último período centralizado informado: ${nombreMes(mesNum(upcTxt))} ${upcTxt.slice(0, 4)}.` : "Aequor no informa un mes con la contabilidad completa."),
          accion: cuadraTotal ? null : faltan.length ? `Centralizar ${listaMeses(faltan)} y volver a cargar los datos de Aequor.` : "Revisar la diferencia entre las ventas contables y las del F29.",
          mensajeCliente:
            !cuadraTotal && estado !== "BLOQUEO"
              ? `La contabilidad está registrada hasta ${nombreMes(base) || "el inicio del año"}. Los saldos contables de este informe son preliminares y se actualizarán al centralizar ${faltan.length ? listaMeses(faltan) : "los meses pendientes"}.`
              : null,
          seccion: "prebalance",
        })
      );
    }
  }

  // V05 RCV compras vs F29
  {
    const control = "Registro de compras vs F29 (cód. 520 − 528 y 525)";
    const comp = E?.rcv?.compras;
    if (!comp) {
      out.push(V("V05", control, "OMITIDA", { mensajeInterno: "Sin registro de compras de Aequor." }));
    } else {
      const porPer = Object.fromEntries(comp.map((k) => [mesNum(k.periodo), k]));
      let erpTot = 0;
      let ofiTot = 0;
      const difs = [];
      decl.forEach((r) => {
        const k = porPer[r.mes] || {};
        const erpCred = n(k.ivaRecuperable) - n(k.ncIva) + n(k.ndIva);
        const ofiCred = f29(r, "520") - f29(r, "528") + f29(r, "532");
        const erpAF = n(k.activoFijoIva);
        const ofiAF = f29(r, "525");
        erpTot += erpCred + erpAF;
        ofiTot += ofiCred + ofiAF;
        const dC = Math.abs(erpCred - ofiCred) > tol(ofiCred);
        const dA = Math.abs(erpAF - ofiAF) > tol(ofiAF);
        if (dC || dA) {
          difs.push({
            mes: r.mes,
            txt: [dC ? `crédito ${clp(erpCred)} vs ${clp(ofiCred)}` : "", dA ? `activo fijo ${clp(erpAF)} vs ${clp(ofiAF)}` : ""].filter(Boolean).join(", "),
          });
        }
      });
      out.push(
        V("V05", control, difs.length ? "ADVERTENCIA" : "OK", {
          valorERP: Math.round(erpTot), valorOficial: Math.round(ofiTot),
          mensajeInterno: difs.length
            ? `Diferencias (registro vs F29): ${difs.map((d) => `${nombreMes(d.mes).slice(0, 3)}: ${d.txt}`).join(" · ")}.`
            : "El IVA crédito del registro de compras cuadra con los F29 mes a mes.",
          accion: difs.length ? "Conciliar el registro de compras con los F29 y rectificar si corresponde." : null,
          mensajeCliente: difs.length
            ? `El IVA de sus compras registrado en ${listaMeses(difs.map((d) => d.mes))} no coincide con lo declarado en el F29. Lo estamos revisando; si corresponde, se rectificará.`
            : null,
          seccion: "f29",
        })
      );
    }
  }

  // V06 RCV ventas vs F29
  {
    const control = "Registro de ventas vs F29 (cód. 538)";
    const ven = E?.rcv?.ventas;
    if (!ven) {
      out.push(V("V06", control, "OMITIDA", { mensajeInterno: "Sin registro de ventas de Aequor." }));
    } else {
      const porPer = Object.fromEntries(ven.map((k) => [mesNum(k.periodo), k]));
      let erpTot = 0;
      let ofiTot = 0;
      const difs = [];
      decl.forEach((r) => {
        const k = porPer[r.mes] || {};
        const erp = n(k.iva) - n(k.ncIva) + n(k.ndIva);
        const ofi = f29(r, "538");
        erpTot += erp;
        ofiTot += ofi;
        if (Math.abs(erp - ofi) > tol(ofi)) difs.push({ mes: r.mes, txt: `${clp(erp)} vs ${clp(ofi)}` });
      });
      out.push(
        V("V06", control, difs.length ? "ADVERTENCIA" : "OK", {
          valorERP: Math.round(erpTot), valorOficial: Math.round(ofiTot),
          mensajeInterno: difs.length
            ? `IVA débito (registro vs F29): ${difs.map((d) => `${nombreMes(d.mes).slice(0, 3)}: ${d.txt}`).join(" · ")}. Si vende con boleta, revise que el resumen de boletas esté en el registro.`
            : "El IVA débito del registro de ventas cuadra con los F29 mes a mes.",
          accion: difs.length ? "Revisar documentos faltantes, duplicados o notas de crédito en el registro de ventas." : null,
          mensajeCliente: difs.length
            ? `Las ventas registradas en ${listaMeses(difs.map((d) => d.mes))} no coinciden con las declaradas en el F29. Lo estamos revisando; si corresponde, se rectificará.`
            : null,
          seccion: "f29",
        })
      );
    }
  }

  // V07 Remuneraciones vs código 48
  {
    const control = "Remuneraciones vs impuesto único F29 (cód. 48)";
    const rem = E?.remuneraciones;
    const f48 = sumF29("48");
    if (!rem) {
      out.push(V("V07", control, "OMITIDA", { valorOficial: f48, mensajeInterno: "Sin datos de remuneraciones: se usa el campo manual del panel." }));
    } else {
      const erp = rem.filter((q) => enCorte(q.periodo)).reduce((a, q) => a + n(q.impuestoUnico), 0);
      let estado = "OK";
      let msg = "";
      if (!rem.length) {
        estado = f48 > 0 ? "ADVERTENCIA" : "OK";
        msg = f48 > 0 ? `El F29 informa ${clp(f48)} de impuesto único (código 48), pero Aequor no tiene liquidaciones de sueldo: falta información de remuneraciones.` : "Sin trabajadores ni impuesto único.";
      } else {
        estado = Math.abs(erp - f48) > tol(f48) ? "ADVERTENCIA" : "OK";
        msg = `Impuesto único de las liquidaciones ${clp(erp)} vs código 48 ${clp(f48)}${estado === "OK" ? ". Cuadra." : "."}`;
      }
      out.push(
        V("V07", control, estado, {
          valorERP: erp, valorOficial: f48, mensajeInterno: msg,
          accion: estado === "OK" ? null : rem.length ? "Conciliar las liquidaciones con el F29." : "Cargar las liquidaciones de sueldo en Aequor o confirmar el gasto en el panel.",
        })
      );
    }
  }

  // V08 Cuadratura del balance
  {
    const control = "Cuadratura del balance";
    if (!s || !has(s.totalActivos) || !has(s.patrimonio)) {
      out.push(V("V08", control, "OMITIDA", { mensajeInterno: "Sin totales del balance." }));
    } else {
      const dif = n(s.totalActivos) - n(s.totalPasivos) - n(s.patrimonio);
      const ok = Math.abs(dif) <= tol(n(s.totalActivos));
      out.push(
        V("V08", control, ok ? "OK" : "ADVERTENCIA", {
          valorERP: n(s.totalActivos), valorOficial: n(s.totalPasivos) + n(s.patrimonio), diferencia: Math.round(dif),
          mensajeInterno: `Activos ${clp(s.totalActivos)} − pasivos ${clp(s.totalPasivos)} − patrimonio ${clp(s.patrimonio)} (incluye el resultado) = ${clp(dif)}.`,
          accion: ok ? null : "Revisar saldos iniciales y asientos descuadrados.",
        })
      );
    }
  }

  // V09 Deuda TGR registrada
  {
    const control = "Deuda TGR registrada en la contabilidad";
    if (!T) out.push(V("V09", control, "OMITIDA", { mensajeInterno: "Sin certificado de deuda de la TGR." }));
    else if (!s || !has(s.impuestosPorPagar)) out.push(V("V09", control, "OMITIDA", { mensajeInterno: "Sin saldo de impuestos por pagar en Aequor." }));
    else {
      const deuda = n(T.totalMorosa) + n(T.totalNoVencida);
      const imp = n(s.impuestosPorPagar);
      const ok = deuda === 0 || imp >= deuda - tol(deuda);
      out.push(
        V("V09", control, ok ? "OK" : "ADVERTENCIA", {
          valorERP: imp, valorOficial: deuda,
          mensajeInterno: deuda === 0 ? "Sin deuda en la TGR." : `Impuestos por pagar en la contabilidad ${clp(imp)} vs deuda TGR (morosa + no vencida) ${clp(deuda)}.`,
          accion: ok ? null : "Registrar la deuda con la TGR (IVA, PPM, retenciones y recargos) en la contabilidad.",
        })
      );
    }
  }

  // V10 Retiros vs art. 14 E
  {
    const control = "Retiros de socios vs incentivo 14 E";
    const retiros = n(s?.retirosSocios);
    const estim = n(P?.retirosEstimados);
    if (rk !== "14D3") out.push(V("V10", control, "OMITIDA", { mensajeInterno: "El incentivo 14 E solo se proyecta en Pro Pyme General." }));
    else if (!retiros && !estim) out.push(V("V10", control, "OK", { valorERP: 0, mensajeInterno: "Sin retiros registrados ni estimados." }));
    else {
      out.push(
        V("V10", control, "INFO", {
          valorERP: retiros, valorOficial: null, diferencia: null,
          mensajeInterno: `Retiros registrados en el año: ${clp(retiros)} · usados en la proyección: ${clp(estim)}. La rebaja del 14 E se calcula sobre la base menos los retiros.`,
          accion: retiros > estim ? "Actualizar «Retiros de socios estimados» en el panel con al menos lo ya retirado." : null,
        })
      );
    }
  }

  // V11 Asesor responsable
  {
    const nombre = String(P?.asesor || "").trim();
    const generico = asesorGenerico(nombre);
    out.push(
      V("V11", "Asesor responsable", generico ? "BLOQUEO" : "OK", {
        valorERP: null,
        mensajeInterno: generico ? `Como asesor figura «${nombre || "(vacío)"}», que es un usuario del sistema y no la persona responsable.` : `Asesor: ${nombre}.`,
        accion: generico ? "Poner el nombre de la persona responsable en «Identificación del informe»." : null,
      })
    );
  }

  // V12 Caja
  {
    const control = "Caja y bancos no negativa";
    if (!s || !has(s.cajaBancos)) out.push(V("V12", control, "OMITIDA", { mensajeInterno: "Sin saldos contables." }));
    else {
      const ok = n(s.cajaBancos) >= 0;
      out.push(
        V("V12", control, ok ? "OK" : "ADVERTENCIA", {
          valorERP: n(s.cajaBancos),
          mensajeInterno: ok ? `Caja y bancos ${clp(s.cajaBancos)}.` : `Caja y bancos negativa (${clp(s.cajaBancos)}): faltan ingresos o hay pagos duplicados.`,
          accion: ok ? null : "Conciliar las cuentas bancarias con las cartolas.",
        })
      );
    }
  }
  return out;
}

export const ESTADOS_VALIDACION = {
  OK: { etiqueta: "OK", orden: 4 },
  INFO: { etiqueta: "Informativa", orden: 3 },
  OMITIDA: { etiqueta: "Omitida", orden: 5 },
  ADVERTENCIA: { etiqueta: "Advertencia", orden: 2 },
  BLOQUEO: { etiqueta: "Bloqueo", orden: 1 },
};
